import { parseEmailList, parseSingleEmail } from "@/lib/email/utils/parse-recipients";

/**
 * The single shared mailbox every form email is sent from and archived to. Per-event
 * routing is layered on top via plus-addressing (see `buildFormEventSenderAddress`),
 * so all form mail still lands in — and is BCC'd to — this one inbox.
 */
export const FORMS_EVENTS_MAILBOX = "events@nowadays.ai";

const FORMS_EVENTS_MAILBOX_LOCAL = "events";
const FORMS_EVENTS_MAILBOX_DOMAIN = "nowadays.ai";

/**
 * Builds the per-event sender / reply-to address for a form email.
 *
 * Form emails are sent from — and have their reply-to set to — a plus-addressed alias
 * of the shared events mailbox, e.g. `events+1867@nowadays.ai` for event 1867. The
 * `+<event_id>` tag rides through to inbound replies, letting a reply be routed back to
 * the event that sent it while still delivering to the single `events@nowadays.ai` inbox.
 *
 * Falls back to the bare mailbox when `eventId` is missing or not a positive integer, so
 * a malformed caller can never emit `events+@` or `events+NaN@nowadays.ai`.
 *
 * @param eventId - The event the form belongs to.
 * @returns The plus-addressed sender address, or the bare mailbox when no valid id.
 */
export function buildFormEventSenderAddress(eventId: number | null | undefined): string {
  if (typeof eventId !== "number" || !Number.isInteger(eventId) || eventId <= 0) {
    return FORMS_EVENTS_MAILBOX;
  }
  return `${FORMS_EVENTS_MAILBOX_LOCAL}+${eventId}@${FORMS_EVENTS_MAILBOX_DOMAIN}`;
}

/**
 * Resolves a form email's reply-to from its stored setting.
 *
 * A form's saved reply-to setting wins, EXCEPT when it is the bare shared mailbox
 * (`events@nowadays.ai`) or empty/invalid — those are rewritten to the per-event alias
 * `events+<event_id>@nowadays.ai` so replies stay attributable to the event. This keeps legacy
 * forms (whose stored default is the bare mailbox) routing to the per-event alias, while honoring
 * a form-specific reply-to (e.g. a new form defaulted to its owner, or an explicitly customized
 * address).
 *
 * @param replyToSetting - The form's stored/effective reply-to setting (may be undefined).
 * @param eventId - The event the form belongs to, used to build the per-event alias fallback.
 * @returns The reply-to address to send with.
 */
export function resolveFormReplyTo(
  replyToSetting: string | null | undefined,
  eventId: number | null | undefined
): string {
  const alias = buildFormEventSenderAddress(eventId);
  const parsed = parseSingleEmail(replyToSetting ?? undefined);
  if (!parsed) return alias;
  return parsed.toLowerCase() === FORMS_EVENTS_MAILBOX.toLowerCase() ? alias : parsed;
}

/**
 * Resolves a form email's reply-to setting into one or more addresses to send with.
 *
 * The array-aware counterpart of {@link resolveFormReplyTo}: a form may now store multiple reply-to
 * addresses (comma-separated string or array). Each configured address is honored, EXCEPT the bare
 * shared mailbox (`events@nowadays.ai`), which is rewritten to the per-event alias
 * `events+<event_id>@nowadays.ai` so replies stay attributable to the event. When the setting is
 * empty or has no valid address, falls back to the single per-event alias.
 *
 * @param replyToSetting - The form's stored/effective reply-to (single address, comma-separated list, or array).
 * @param eventId - The event the form belongs to, used to build the per-event alias fallback.
 * @returns A non-empty, deduped list of reply-to addresses.
 */
export function resolveFormReplyToList(
  replyToSetting: string | string[] | null | undefined,
  eventId: number | null | undefined
): string[] {
  const alias = buildFormEventSenderAddress(eventId);
  const raw = Array.isArray(replyToSetting) ? replyToSetting.join(",") : (replyToSetting ?? "");
  const parsed = parseEmailList(raw);
  if (parsed.length === 0) return [alias];

  const seen = new Set<string>();
  const out: string[] = [];
  for (const address of parsed) {
    const resolved = address.toLowerCase() === FORMS_EVENTS_MAILBOX.toLowerCase() ? alias : address;
    const key = resolved.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(resolved);
  }
  return out;
}
